/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / MAIL-KINDS — Single-Source-Katalog aller versendbaren Mail-Kinds
   =============================================================================
   S225 CUT A/B (Mail-Rework). Eine Wahrheitsquelle für:

     - CUT A: Übersichtsliste + Modal (Label, Säule/Chip, Trigger-Klartext,
       Settings-Slot/-Field für Override + Default-Lookup, Platzhalter-Chips).
     - CUT B: Toggle pro Mail-Kind. `settings.mailKindEnabled[<kind>]` — fehlender
       Key = an (Default true). Spiegel des SQL-Helpers
       `gg_mail_kind_enabled(restaurant_id, kind)` (phase_165).

   Die 16 `kind`-Keys sind EXAKT die KIND_TEMPLATES-Keys der Edge-Function
   `supabase/functions/process-mail-outbox/index.ts` (= die `mail_outbox.kind`-
   Werte, die die 11 enqueue-Funktionen schreiben). `settingsKey`/`settingsField`
   bilden auf den (überschreibbaren) Vorlagen-Slot ab — mehrere Kinds dürfen sich
   einen Slot teilen (order_receipt + order_receipt_resend →
   emailTemplatesOrder.receipt).

   BEWUSST OHNE IMPORTS: dieser Katalog + der Toggle-Resolver müssen von
   email-templates.js (JS-Direct-Send-Spiegel) importierbar sein, OHNE einen
   Import-Zyklus über mail-template-defaults.js → email-templates.js auszulösen
   (TDZ-Crash beim Laden). Die Default-/Preview-abhängigen Helfer leben deshalb
   in mail-kind-preview.js.
============================================================================ */

/** Säulen (= Filter-Chips der Übersichtsliste). */
export const MAIL_KIND_PILLARS = Object.freeze({
  reservations: { label: 'Reservierungen', icon: '📅' },
  orders:       { label: 'Bestellungen',   icon: '🍔' },
  delivery:     { label: 'Lieferungen',    icon: '🛵' },
  bons:         { label: 'Belege',         icon: '🧾' },
  exporte:      { label: 'Exporte',        icon: '📤' },
  newsletter:   { label: 'Newsletter',     icon: '📰' },
  // S262 — interne Mitarbeiter-Mails (Dienstplan-Zyklus).
  team:         { label: 'Team',           icon: '🗓️' },
});

/** Chip-Leiste (Reihenfolge + „Alle"-Default). CUT C/D hängen später eigene
 *  Chips an (Eigene Mails · Kampagnen). */
export const MAIL_KIND_CHIPS = Object.freeze([
  { id: 'all',          label: 'Alle',           icon: '✉️' },
  { id: 'reservations', label: 'Reservierungen', icon: '📅' },
  { id: 'orders',       label: 'Bestellungen',   icon: '🍔' },
  { id: 'delivery',     label: 'Lieferungen',    icon: '🛵' },
  { id: 'bons',         label: 'Belege',         icon: '🧾' },
  { id: 'exporte',      label: 'Exporte',        icon: '📤' },
  { id: 'newsletter',   label: 'Newsletter',     icon: '📰' },
  { id: 'team',         label: 'Team',           icon: '🗓️' },
]);

// Token-Sätze pro Säule (Platzhalter-Chip-Leiste im Edit-Modus).
const TOKENS_RESERVATION = Object.freeze(['{{name}}', '{{guests}}', '{{reservation_date}}', '{{reservation_time}}', '{{cancel_link}}', '{{brand}}']);
const TOKENS_RESERVATION_COMBINED = Object.freeze(['{{name}}', '{{guests}}', '{{reservation_date}}', '{{reservation_time}}', '{{items_block}}', '{{total}}', '{{cancel_link}}', '{{brand}}']);
const TOKENS_ORDER = Object.freeze(['{{name}}', '{{order_number}}', '{{pickup_date}}', '{{pickup_time}}', '{{total}}', '{{items}}', '{{tracking_link}}', '{{cancel_link}}', '{{brand}}']);
const TOKENS_RECEIPT = Object.freeze(['{{name}}', '{{order_number}}', '{{invoice_number}}', '{{payment_method}}', '{{total}}', '{{items}}', '{{brand}}']);
const TOKENS_EXPORT = Object.freeze(['{{zbon_number}}', '{{date}}', '{{gross}}', '{{count}}', '{{brand}}']);
const TOKENS_NEWSLETTER = Object.freeze(['{{confirm_link}}', '{{brand}}']);
// S262 — Dienstplan-Mails: Mitarbeiter-Name, ISO-KW, Wochenspanne, Deadline (Wanduhr), Link.
const TOKENS_SCHEDULE = Object.freeze(['{{name}}', '{{week_no}}', '{{week_range}}', '{{deadline}}', '{{schedule_link}}', '{{brand}}']);
// S264 — Plan-Mail: Version, eigene Schichten, Woche als Text (HTML: Tabelle).
const TOKENS_SCHEDULE_PUBLISHED = Object.freeze(['{{name}}', '{{week_no}}', '{{week_range}}', '{{version}}', '{{my_shifts}}', '{{plan}}', '{{brand}}']);

/**
 * Die 20 Mail-Kinds (16 System + newsletter_confirm + 3 Dienstplan, S262/S264),
 * gruppiert nach Säule (Anzeige-Reihenfolge in „Alle").
 *   kind          KIND_TEMPLATES-Key = mail_outbox.kind = mailKindEnabled-Key
 *   label         Anzeige-Name
 *   pillar        Säule / Chip-Zuordnung
 *   settingsKey   Override-/Default-/Reset-Slot (emailTemplates*)
 *   settingsField Feld innerhalb des Slots
 *   trigger       Auslöser in Klartext
 *   critical      GoBD-/Vertrags-relevant → Inline-Warnung am Toggle
 *   tokens        Platzhalter-Chips im Edit-Modus
 *   note          optionaler Hinweis (z. B. geteilter Vorlagen-Slot)
 *   hasLeadHours  Reminder-Zeile mit Vorlaufzeit-Dropdown
 */
export const MAIL_KINDS = Object.freeze([
  // ===== Reservierungen =====
  {
    kind: 'reservation_confirmed_customer', label: 'Reservierungsbestätigung', pillar: 'reservations',
    settingsKey: 'emailTemplates', settingsField: 'confirmed',
    trigger: 'Bei Annahme der Reservierung', critical: false, tokens: TOKENS_RESERVATION,
  },
  {
    kind: 'reservation_with_order_confirmed', label: 'Bestätigung mit Vorbestellung', pillar: 'reservations',
    settingsKey: 'emailTemplates', settingsField: 'confirmed_with_order',
    trigger: 'Bei Annahme einer Reservierung mit Vorbestellung', critical: false, tokens: TOKENS_RESERVATION_COMBINED,
  },
  {
    kind: 'reservation_declined_customer', label: 'Reservierungsabsage', pillar: 'reservations',
    settingsKey: 'emailTemplates', settingsField: 'declined',
    trigger: 'Bei Ablehnung der Reservierung', critical: false, tokens: TOKENS_RESERVATION,
  },
  {
    kind: 'reservation_cancelled_customer', label: 'Reservierungs-Storno', pillar: 'reservations',
    settingsKey: 'emailTemplates', settingsField: 'cancelled',
    trigger: 'Bei Storno der Reservierung', critical: true, tokens: TOKENS_RESERVATION,
  },
  {
    kind: 'reservation_reminder_customer', label: 'Erinnerungsmail', pillar: 'reservations',
    settingsKey: 'emailTemplates', settingsField: 'reminder',
    trigger: 'Automatisch vor der Reservierung', critical: false, tokens: TOKENS_RESERVATION,
    hasLeadHours: true,
  },
  // ===== Bestellungen =====
  {
    kind: 'order_confirmed_customer', label: 'Bestellbestätigung', pillar: 'orders',
    settingsKey: 'emailTemplatesOrder', settingsField: 'confirmed',
    trigger: 'Bei Klick auf „Annehmen“', critical: false, tokens: TOKENS_ORDER,
  },
  {
    kind: 'order_ready_customer', label: 'Abholbereit', pillar: 'orders',
    settingsKey: 'emailTemplatesOrder', settingsField: 'ready',
    trigger: 'Bei Klick auf „Abholbereit“', critical: false, tokens: TOKENS_ORDER,
  },
  {
    kind: 'order_cancelled_customer', label: 'Bestell-Storno', pillar: 'orders',
    settingsKey: 'emailTemplatesOrder', settingsField: 'cancelled',
    trigger: 'Bei Storno der Bestellung', critical: true, tokens: TOKENS_ORDER,
  },
  // ===== Lieferungen =====
  {
    kind: 'delivery_confirmed_customer', label: 'Lieferbestätigung', pillar: 'delivery',
    settingsKey: 'emailTemplatesDelivery', settingsField: 'confirmed',
    trigger: 'Bei Annahme der Lieferung', critical: false, tokens: TOKENS_ORDER,
  },
  {
    kind: 'delivery_ready_customer', label: 'Unterwegs', pillar: 'delivery',
    settingsKey: 'emailTemplatesDelivery', settingsField: 'ready',
    trigger: 'Bei „In Auslieferung“', critical: false, tokens: TOKENS_ORDER,
  },
  {
    kind: 'delivery_cancelled_customer', label: 'Liefer-Storno', pillar: 'delivery',
    settingsKey: 'emailTemplatesDelivery', settingsField: 'cancelled',
    trigger: 'Bei Storno der Lieferung', critical: true, tokens: TOKENS_ORDER,
  },
  {
    kind: 'delivery_receipt', label: 'Zugestellt + Beleg', pillar: 'delivery',
    settingsKey: 'emailTemplatesDelivery', settingsField: 'receipt',
    trigger: 'Bei „Zugestellt“ — Bon-PDF im Anhang', critical: true, tokens: TOKENS_ORDER,
  },
  // ===== Belege =====
  {
    kind: 'order_receipt', label: 'Kassenbon', pillar: 'bons',
    settingsKey: 'emailTemplatesOrder', settingsField: 'receipt',
    trigger: 'Nach „Bezahlt“ — Kassenbon-PDF im Anhang', critical: true, tokens: TOKENS_RECEIPT,
  },
  {
    kind: 'order_receipt_correction', label: 'Stornobeleg', pillar: 'bons',
    settingsKey: 'emailTemplatesOrder', settingsField: 'correction',
    trigger: 'Bei Erstattung/Korrektur — Stornobeleg-PDF im Anhang', critical: true, tokens: TOKENS_RECEIPT,
  },
  {
    kind: 'order_receipt_resend', label: 'Beleg erneut senden', pillar: 'bons',
    settingsKey: 'emailTemplatesOrder', settingsField: 'receipt',
    trigger: 'Manueller Erneut-Versand aus dem Archiv', critical: true, tokens: TOKENS_RECEIPT,
    note: 'Nutzt dieselbe Vorlage wie der Kassenbon (geteilter Vorlagen-Slot).',
  },
  // ===== Exporte =====
  {
    kind: 'zbon_receipt', label: 'Z-Bon an Steuerberater', pillar: 'exporte',
    settingsKey: 'emailTemplatesExport', settingsField: 'zbon',
    trigger: 'Beim Tagesabschluss — Z-Bon-PDF im Anhang', critical: true, tokens: TOKENS_EXPORT,
  },
  // ===== Newsletter (S226) — Double-Opt-in-Bestätigung, NICHT abschaltbar =====
  {
    kind: 'newsletter_confirm', label: 'Abo-Bestätigung (Double-Opt-in)', pillar: 'newsletter',
    settingsKey: 'emailTemplatesNewsletter', settingsField: 'confirm',
    trigger: 'Bei Newsletter-Anmeldung — werbefreie Bestätigungsmail', critical: false,
    tokens: TOKENS_NEWSLETTER, noToggle: true,
    note: 'Immer aktiv — ohne diese Bestätigungsmail ist kein Abo möglich (gesetzlich vorgeschriebenes Double-Opt-in).',
  },
  // ===== Team (S262) — Dienstplan-Zyklus, interne Mitarbeiter-Mails =====
  {
    kind: 'schedule_request', label: 'Dienstplan: Verfügbarkeit abfragen', pillar: 'team',
    settingsKey: 'emailTemplatesSchedule', settingsField: 'request',
    trigger: 'Automatisch (Do 10:00, Einstellung) — Token-Link für die Folgewoche', critical: false,
    tokens: TOKENS_SCHEDULE,
  },
  {
    kind: 'schedule_reminder', label: 'Dienstplan: Erinnerung', pillar: 'team',
    settingsKey: 'emailTemplatesSchedule', settingsField: 'reminder',
    trigger: 'Automatisch (Sa 10:00, Einstellung) — nur an Säumige', critical: false,
    tokens: TOKENS_SCHEDULE,
  },
  {
    kind: 'schedule_published', label: 'Dienstplan: Plan veröffentlicht', pillar: 'team',
    settingsKey: 'emailTemplatesSchedule', settingsField: 'published',
    trigger: 'Bei „Veröffentlichen" im Dienstplan — Woche als Tabelle, PDF im Anhang', critical: false,
    tokens: TOKENS_SCHEDULE_PUBLISHED,
    note: 'Jedes erneute Veröffentlichen zählt die Version hoch (v2, v3 …) und mailt alle Teilnehmer erneut.',
  },
]);

/** Kind-Katalog-Lookup. Null bei unbekanntem Kind. */
export function getMailKind(kind) {
  return MAIL_KINDS.find(k => k.kind === kind) || null;
}

/** Kinds einer Chip-Kategorie („all" → alle 16, sonst gefiltert nach Säule). */
export function mailKindsForChip(chipId) {
  if (!chipId || chipId === 'all') return MAIL_KINDS.slice();
  return MAIL_KINDS.filter(k => k.pillar === chipId);
}

/**
 * CUT-B-Toggle-Resolver (Spiegel des SQL-Helpers gg_mail_kind_enabled).
 * Fehlender Key = an. Nur ein explizites `false` deaktiviert.
 */
export function isMailKindEnabled(settings, kind) {
  const map = settings && settings.mailKindEnabled;
  if (!map || typeof map !== 'object') return true;
  return map[kind] === false ? false : true;
}
